import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Trash2Icon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ButtonGroup } from "./button-group";
import { Matcher } from "react-day-picker";
import useActiveLocale from "@/hooks/useActiveLocale";
import useActiveLanguage from "@/hooks/useActiveLanguage";
import { Trans } from "@lingui/react/macro";

type DatePickerProps = {
  onChangeDate: (date: Date | undefined) => void;
  clearLabel?: React.ReactNode;
  isClearable?: boolean;
  disabled?: Matcher | Matcher[];
  value?: Date;
  id?: string;
  "aria-invalid"?: boolean;
};

export default function DatePicker({
  clearLabel,
  onChangeDate,
  isClearable,
  disabled,
  value,
  id,
  ...rest
}: DatePickerProps) {
  const [open, setOpen] = React.useState<boolean>(false);

  const language = useActiveLanguage();
  const locale = useActiveLocale(language);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ButtonGroup className="w-full">
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            data-empty={!value}
            className="data-[empty=true]:text-muted-foreground flex-1 justify-start text-left font-normal"
            aria-invalid={rest["aria-invalid"]}
          >
            <CalendarIcon />
            {value ? (
              format(value, "PPP", { locale })
            ) : (
              <span>
                <Trans>Välj datum</Trans>
              </span>
            )}
          </Button>
        </PopoverTrigger>

        {isClearable && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => {
              onChangeDate(undefined);
            }}
            aria-invalid={rest["aria-invalid"]}
          >
            <Trash2Icon />
            {clearLabel && <span className="sr-only">{clearLabel}</span>}
          </Button>
        )}
      </ButtonGroup>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          disabled={disabled}
          onSelect={(newValue) => {
            setOpen(false);
            onChangeDate(newValue);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
